import React, { useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { gridSx, LoadingOverlay } from "../NewLeads/NewLeads";
import {
  getLoyaltyAnalytics,
  getLoyaltyClinicList,
  getLoyaltyCategory,
  getAllocatedPoints,
  getPendingClaims,
  getEarnedPoints,
  getPendingRedeemed,
  getRedeemedPoints,
  approveClaim,
  declineClaim,
  resubmitClaim,
  approveRedeemed,
} from "../../services/loyaltyPoints";

// ── Types ──────────────────────────────────────────────────────────────────

interface LoyaltyItem {
  id: number;
  actionId: number;
  patient: string;
  phone: string;
  email: string;
  clinicName: string | null;
  type: string;
  points: number;
  source: string | null;
  date: string;
  referredBy?: string | null;
}

interface Analytics {
  allocatedPoints: { totalPoints: number; transactions: number };
  pendingClaims: { totalClaims: number; ptsAwaiting: number };
  earnedPoints: { totalPoints: number; approved: number };
  redeemed: { totalPoints: number; redemptions: number };
}

interface Filters {
  clinicId: string;
  category: string;
  search: string;
  startDate: string;
  endDate: string;
}

const EMPTY_FILTERS: Filters = {
  clinicId: "",
  category: "",
  search: "",
  startDate: "",
  endDate: "",
};

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) => (n ?? 0).toLocaleString();

const fmtDate = (d: string) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// ── Cell Renderers ─────────────────────────────────────────────────────────

const PatientCell = ({
  name,
  phone,
  email,
}: {
  name: string;
  phone: string;
  email: string;
}) => (
  <div style={{ lineHeight: 1.2, padding: "4px 0" }}>
    <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>
      {name}
    </div>
    <div style={{ fontSize: 10, color: "#9ca3af" }}>{phone}</div>
    <div style={{ fontSize: 10, color: "#9ca3af" }}>{email}</div>
  </div>
);

const TypeBadge = ({ label }: { label: string }) => (
  <span
    style={{
      border: "1px solid #d1d5db",
      borderRadius: 16,
      padding: "2px 10px",
      fontSize: 12,
      color: "#374151",
      whiteSpace: "nowrap" as const,
    }}
  >
    {label}
  </span>
);

const PointsCell = ({ value }: { value: number }) => (
  <span
    style={{
      fontWeight: 700,
      fontSize: 13,
      color: value < 0 ? "#e53935" : "#16a09a",
    }}
  >
    {value > 0 ? "+" : ""}
    {fmt(value)}
  </span>
);

// ── Reason Dialog ──────────────────────────────────────────────────────────

const ReasonDialog = ({
  open,
  title,
  onClose,
  onConfirm,
  loading,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
}) => {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        style={{
          backgroundColor: "#194e5b",
          color: "#fff",
          fontSize: 15,
          fontWeight: 700,
          padding: "14px 20px",
          position: "relative",
        }}
      >
        {title}
        <IconButton
          onClick={onClose}
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#fff",
          }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: 13 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ padding: "20px" }}>
        <TextField
          label="Reason"
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          style={{ marginTop: 8 }}
        />
      </DialogContent>
      <DialogActions style={{ padding: "10px 20px" }}>
        <Button onClick={onClose} size="small" style={{ color: "#6b7280" }}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(reason)}
          disabled={!reason.trim() || loading}
          size="small"
          variant="contained"
          style={{ backgroundColor: "#194e5b" }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Action Buttons ─────────────────────────────────────────────────────────

const ClaimActionButtons = ({
  actionId,
  onDone,
  showResubmit,
}: {
  actionId: number;
  onDone: () => void;
  showResubmit?: boolean;
}) => {
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<"decline" | "resubmit" | null>(null);

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const res = await approveClaim(actionId);
      if (res && res.status && !res.status.error) onDone();
    } finally {
      setBusy(false);
    }
  };

  const handleReason = async (reason: string) => {
    setBusy(true);
    try {
      const fn = dialog === "decline" ? declineClaim : resubmitClaim;
      const res = await fn(actionId, reason);
      if (res && res.status && !res.status.error) {
        setDialog(null);
        onDone();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          whiteSpace: "nowrap" as const,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleApprove}
          disabled={busy}
          style={{
            border: "none",
            background: "none",
            color: "#16a09a",
            fontWeight: 600,
            fontSize: 12,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: 0,
          }}
        >
          <i className="bi bi-check-circle" /> Approve
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDialog("decline");
          }}
          disabled={busy}
          style={{
            border: "none",
            background: "none",
            color: "#e53935",
            fontWeight: 600,
            fontSize: 12,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: 0,
          }}
        >
          <i className="bi bi-x-circle" /> Decline
        </button>
        {showResubmit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDialog("resubmit");
            }}
            disabled={busy}
            style={{
              border: "none",
              background: "none",
              color: "#6b7280",
              fontWeight: 600,
              fontSize: 12,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: 0,
            }}
          >
            <i className="bi bi-arrow-counterclockwise" /> Resubmit
          </button>
        )}
      </div>
      <ReasonDialog
        open={dialog === "decline"}
        title="Decline Claim"
        onClose={() => setDialog(null)}
        onConfirm={handleReason}
        loading={busy}
      />
      <ReasonDialog
        open={dialog === "resubmit"}
        title="Resubmit Claim"
        onClose={() => setDialog(null)}
        onConfirm={handleReason}
        loading={busy}
      />
    </>
  );
};

const RedeemActionButton = ({
  redeemedId,
  onDone,
}: {
  redeemedId: number;
  onDone: () => void;
}) => {
  const [busy, setBusy] = useState(false);
  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const res = await approveRedeemed(redeemedId);
      if (res && res.status && !res.status.error) onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        onClick={handleApprove}
        disabled={busy}
        style={{
          border: "none",
          background: "none",
          color: "#16a09a",
          fontWeight: 600,
          fontSize: 12,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: 0,
        }}
      >
        <i className="bi bi-check-circle" /> Approve
      </button>
    </div>
  );
};

// ── Tab data hook ──────────────────────────────────────────────────────────

function useTabData(
  fetcher: (page: number, pageSize: number, filters: Filters) => Promise<any>,
  enabled: boolean,
  filters: Filters
) {
  const [rows, setRows] = useState<LoyaltyItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Reset to page 0 when filters change
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      prevFiltersRef.current = filters;
      setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }
  }, [filters]);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    fetcherRef
      .current(pagination.page + 1, pagination.pageSize, filters)
      .then((res) => {
        if (res && res.status && !res.status.error) {
          setRows(
            (res.data || []).map((r: any, i: number) => ({
              ...r,
              id: r.actionId ?? i,
            }))
          );
          setTotal(res.total ?? 0);
        }
      })
      .finally(() => setLoading(false));
  }, [enabled, pagination.page, pagination.pageSize, filters, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  return { rows, total, loading, pagination, setPagination, refresh };
}

// ── Tab Table ──────────────────────────────────────────────────────────────

const TabTable = ({
  title,
  subtitle,
  badgeTeal,
  data,
  columns,
  search,
  onSearchChange,
  showSearch,
}: {
  title: string;
  subtitle: string;
  badgeTeal: boolean;
  data: ReturnType<typeof useTabData>;
  columns: GridColDef[];
  search: string;
  onSearchChange: (v: string) => void;
  showSearch: boolean;
}) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      margin: 20,
      position: "relative",
    }}
  >
    {data.loading && <LoadingOverlay />}

    {/* Card header */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        borderBottom: "1px solid #f3f4f6",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 17, color: "#111827" }}>
          {title}
        </span>
        <span
          style={{
            borderRadius: 12,
            padding: "2px 10px",
            fontSize: 12,
            fontWeight: 600,
            ...(badgeTeal
              ? {
                  background: "#e6f7f5",
                  color: "#16a09a",
                  border: "1px solid #b2e8e5",
                }
              : {
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                }),
          }}
        >
          {data.total} records
        </span>
        <span style={{ color: "#9ca3af", fontSize: 13 }}>{subtitle}</span>
      </div>

      {showSearch && (
        <div style={{ position: "relative" }}>
          <i
            className="bi bi-search"
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9ca3af",
              fontSize: 13,
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder="Search patients..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "7px 12px 7px 32px",
              fontSize: 13,
              color: "#374151",
              background: "#fff",
              width: 220,
              outline: "none",
            }}
          />
        </div>
      )}
    </div>

    <DataGrid
      rows={data.rows}
      columns={columns}
      disableColumnMenu
      density="standard"
      paginationMode="server"
      rowCount={data.total}
      paginationModel={data.pagination}
      onPaginationModelChange={data.setPagination}
      pageSizeOptions={[20, 50]}
      sx={gridSx}
    />
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────

export const LoyaltyPointsClaimsPage = () => {
  const [activeTab, setActiveTab] = useState("allocated");
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const [categories, setCategories] = useState<{ id: number; title: string }[]>(
    []
  );
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  useEffect(() => {
    getLoyaltyAnalytics().then((res) => {
      if (res && res.status && !res.status.error) setAnalytics(res);
    });
    getLoyaltyClinicList().then((res) => {
      if (res && res.status && !res.status.error) {
        setClinics(
          (res.data || []).map((c: any) => ({
            id: String(c.clinicId ?? c.id ?? ""),
            name: c.clinicName ?? c.name ?? "",
          }))
        );
      }
    });
    getLoyaltyCategory().then((res) => {
      if (res && res.status && !res.status.error) {
        setCategories(
          (res.data || []).map((c: any) => ({ id: c.id, title: c.title }))
        );
      }
    });
  }, []);

  const setFilter = (key: keyof Filters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const hasActiveFilters =
    filters.clinicId ||
    filters.category ||
    filters.search ||
    filters.startDate ||
    filters.endDate;

  // Tabs that support clinic + category filter
  const tabsWithClinicCategory = ["allocated", "claim-pending", "earned"];
  // Tabs that support search
  const tabsWithSearch = ["allocated", "claim-pending", "earned", "redeemed"];

  const showClinicCategory = tabsWithClinicCategory.includes(activeTab);
  const showSearch = tabsWithSearch.includes(activeTab);

  const allocated = useTabData(
    (p, r, f) =>
      getAllocatedPoints({
        page: p,
        record: r,
        clinicId: f.clinicId || undefined,
        category: f.category || undefined,
        search: f.search || undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
      }),
    activeTab === "allocated",
    filters
  );
  const claims = useTabData(
    (p, r, f) =>
      getPendingClaims({
        page: p,
        record: r,
        clinicId: f.clinicId || undefined,
        category: f.category || undefined,
        search: f.search || undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
      }),
    activeTab === "claim-pending",
    filters
  );
  const earned = useTabData(
    (p, r, f) =>
      getEarnedPoints({
        page: p,
        record: r,
        clinicId: f.clinicId || undefined,
        category: f.category || undefined,
        search: f.search || undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
      }),
    activeTab === "earned",
    filters
  );
  const redeemPending = useTabData(
    (p, r, f) =>
      getPendingRedeemed({
        page: p,
        record: r,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
      }),
    activeTab === "redeemed-pending",
    filters
  );
  const redeemed = useTabData(
    (p, r, f) =>
      getRedeemedPoints({
        page: p,
        record: r,
        search: f.search || undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
      }),
    activeTab === "redeemed",
    filters
  );

  // ── Columns ──────────────────────────────────────────────────────────────

  const baseColumns: GridColDef[] = [
    {
      field: "clinicName",
      headerName: "Clinic",
      minWidth: 200,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {(p.value as string) || "—"}
        </span>
      ),
    },
    {
      field: "patient",
      headerName: "Patient",
      minWidth: 250,
      renderCell: (p) => (
        <PatientCell
          name={p.row.patient}
          phone={p.row.phone}
          email={p.row.email}
        />
      ),
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 200,
      renderCell: (p) => <TypeBadge label={p.value as string} />,
    },
    {
      field: "points",
      headerName: "Points",
      minWidth: 100,
      renderCell: (p) => <PointsCell value={p.value as number} />,
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 120,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {(p.value as string) || "—"}
        </span>
      ),
    },
  ];

  const allocatedColumns: GridColDef[] = [
    ...baseColumns,
    {
      field: "date",
      headerName: "Date",
      minWidth: 160,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {fmtDate(p.value as string)}
        </span>
      ),
    },
  ];

  const claimPendingColumns: GridColDef[] = [
    ...baseColumns,
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 260,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (p) => (
        <ClaimActionButtons
          actionId={p.row.actionId}
          onDone={claims.refresh}
          showResubmit
        />
      ),
    },
    {
      field: "claimDate",
      headerName: "Date",
      minWidth: 160,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {fmtDate(p.value as string)}
        </span>
      ),
    },
  ];

  const earnedColumns: GridColDef[] = [
    {
      field: "patient",
      headerName: "Patient",
      minWidth: 250,
      renderCell: (p) => (
        <PatientCell
          name={p.row.patient}
          phone={p.row.phone}
          email={p.row.email}
        />
      ),
    },
    {
      field: "clinicName",
      headerName: "Clinic",
      minWidth: 200,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {(p.value as string) || "—"}
        </span>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 200,
      renderCell: (p) => <TypeBadge label={p.value as string} />,
    },
    {
      field: "points",
      headerName: "Points",
      minWidth: 100,
      renderCell: (p) => <PointsCell value={p.value as number} />,
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 120,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {(p.value as string) || "—"}
        </span>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      minWidth: 160,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {fmtDate(p.value as string)}
        </span>
      ),
    },
  ];

  const redeemPendingColumns: GridColDef[] = [
    {
      field: "patient",
      headerName: "Patient",
      minWidth: 250,
      renderCell: (p) => (
        <PatientCell
          name={p.row.patient}
          phone={p.row.phone}
          email={p.row.email}
        />
      ),
    },
    {
      field: "clinicName",
      headerName: "Clinic",
      minWidth: 200,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {(p.value as string) || "—"}
        </span>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 200,
      renderCell: (p) => <TypeBadge label={p.value as string} />,
    },
    {
      field: "points",
      headerName: "Points",
      minWidth: 100,
      renderCell: (p) => <PointsCell value={p.value as number} />,
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 120,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {(p.value as string) || "—"}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (p) => (
        <RedeemActionButton
          redeemedId={p.row.actionId}
          onDone={redeemPending.refresh}
        />
      ),
    },
    {
      field: "date",
      headerName: "Date",
      minWidth: 160,
      renderCell: (p) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {fmtDate(p.value as string)}
        </span>
      ),
    },
  ];

  const redeemedColumns: GridColDef[] = [...baseColumns];

  const tabs = [
    {
      key: "allocated",
      label: "Allocated",
      title: "Allocated Points",
      subtitle: "Points allocated but not yet claimed",
      badgeTeal: false,
      data: allocated,
      columns: allocatedColumns,
    },
    {
      key: "claim-pending",
      label: "Claim Pending",
      title: "Claim Pending",
      subtitle: "Patient has requested to claim these points",
      badgeTeal: false,
      data: claims,
      columns: claimPendingColumns,
    },
    {
      key: "earned",
      label: "Earned",
      title: "Earned Points",
      subtitle: "Claim approved — points are now earned",
      badgeTeal: true,
      data: earned,
      columns: earnedColumns,
    },
    {
      key: "redeemed-pending",
      label: "Redeem Pending",
      title: "Redeem Pending",
      subtitle: "Redemption request awaiting approval",
      badgeTeal: false,
      data: redeemPending,
      columns: redeemPendingColumns,
    },
    {
      key: "redeemed",
      label: "Redeemed",
      title: "Redeemed",
      subtitle: "Points redeemed successfully",
      badgeTeal: true,
      data: redeemed,
      columns: redeemedColumns,
    },
  ];

  const activeTabConfig = tabs.find((t) => t.key === activeTab)!;

  const statCards = [
    {
      icon: "bi bi-arrow-left-right",
      label: "Allocated Points",
      value: fmt(analytics?.allocatedPoints.totalPoints ?? 0),
      sub: `${analytics?.allocatedPoints.transactions ?? 0} transactions`,
    },
    {
      icon: "bi bi-clock",
      label: "Pending Claims",
      value: String(analytics?.pendingClaims.totalClaims ?? 0),
      sub: `${fmt(analytics?.pendingClaims.ptsAwaiting ?? 0)} pts awaiting`,
    },
    {
      icon: "bi bi-check-circle",
      label: "Earned Points",
      value: fmt(analytics?.earnedPoints.totalPoints ?? 0),
      sub: `${analytics?.earnedPoints.approved ?? 0} approved`,
    },
    {
      icon: "bi bi-gift",
      label: "Redeemed",
      value: fmt(analytics?.redeemed.totalPoints ?? 0),
      sub: `${analytics?.redeemed.redemptions ?? 0} redemptions`,
    },
  ];

  const dateInputStyle: React.CSSProperties = {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 13,
    color: "#374151",
    background: "#fff",
    outline: "none",
    height: 36,
  };
  const selectMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 280,
        overflowY: "auto !important",
      },
    },
    MenuListProps: {
      sx: {
        maxHeight: 280,
        overflowY: "auto",
      },
    },
  };

  return (
    <>
      <section className="middle-navbar px-30">
        <div className="p-0 container-fluid">
          <div className="d-flex justify-content-between">
            <div className="w-100">
              {/* Header */}
              <div className="d-flex flex-row items-center gap-3 w-100">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: "#e6f7f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginBottom: 14,
                  }}
                >
                  <i
                    className="bi bi-award"
                    style={{ color: "#16a09a", fontSize: 18 }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    Loyalty Points Claims
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 300 }}>
                    Manage point allocations, claims, and redemptions
                  </p>
                </div>
              </div>

              {/* Stat Cards */}
              <div
                className="d-flex flex-row gap-3 w-100 mb-3"
                style={{ flexWrap: "wrap" }}
              >
                {statCards.map((card) => (
                  <div
                    key={card.label}
                    style={{
                      flex: "1 1 180px",
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 14,
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: "#e6f7f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className={card.icon}
                        style={{ color: "#16a09a", fontSize: 18 }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "#6b7280",
                          marginBottom: 2,
                        }}
                      >
                        {card.label}
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: "#111827",
                          lineHeight: 1.2,
                        }}
                      >
                        {card.value}
                      </div>
                      <div
                        style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}
                      >
                        {card.sub}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tabs */}
              <ul className="nav nav-tabs nav-teal cursor-pointer">
                {tabs.map(({ key, label }) => (
                  <li key={key} className="nav-item">
                    <div
                      className={`nav-link${
                        activeTab === key ? " active" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setActiveTab(key);
                        setFilters(EMPTY_FILTERS);
                      }}
                    >
                      {label}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <div
        style={{
          padding: "12px 20px",
          background: "#f9fafb",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {showClinicCategory && (
          <FormControl size="small" style={{ minWidth: 180 }}>
            <InputLabel>Clinic</InputLabel>
            <Select
              value={filters.clinicId}
              label="Clinic"
              MenuProps={selectMenuProps}
              onChange={(e) => setFilter("clinicId", e.target.value as string)}
            >
              <MenuItem value="">
                <em>All Clinics</em>
              </MenuItem>
              {clinics.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {showClinicCategory && (
          <FormControl size="small" style={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              label="Category"
              MenuProps={selectMenuProps}
              onChange={(e) => setFilter("category", e.target.value as string)}
            >
              <MenuItem value="">
                <em>All Categories</em>
              </MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.title}>
                  {c.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <input
          type="date"
          value={filters.startDate}
          onChange={(e) => setFilter("startDate", e.target.value)}
          style={dateInputStyle}
          title="Start Date"
        />
        <span style={{ color: "#9ca3af", fontSize: 13 }}>to</span>
        <input
          type="date"
          value={filters.endDate}
          onChange={(e) => setFilter("endDate", e.target.value)}
          style={dateInputStyle}
          title="End Date"
        />

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 13,
              color: "#6b7280",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 5,
              height: 36,
            }}
          >
            <i className="bi bi-x-circle" /> Clear
          </button>
        )}
      </div>

      {/* Tab Content */}
      <TabTable
        key={activeTab}
        title={activeTabConfig.title}
        subtitle={activeTabConfig.subtitle}
        badgeTeal={activeTabConfig.badgeTeal}
        data={activeTabConfig.data}
        columns={activeTabConfig.columns}
        search={filters.search}
        onSearchChange={(v) => setFilter("search", v)}
        showSearch={showSearch}
      />
    </>
  );
};
